import { useParams } from "react-router-dom";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import { useAppStore } from "store/store";
import usePaxSession from "../usePaxSession";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import ChecklistItem from "models/checklist/checklist-item/checklist-item";

export interface ItemLevelNavigationState {
  itemType: string;
  forceDocumentType?: string;
}

export class ItemNavigationState implements ItemLevelNavigationState {
  public item: ChecklistItem;
  public itemType: string;
  public forceDocumentType?: string;

  public constructor(item: ChecklistItem, forceDocumentType?: string) {
    this.item = item;
    this.itemType = item.itemType;
    this.forceDocumentType = forceDocumentType;
  }
}

export class ItemTypeNavigationState implements ItemLevelNavigationState {
  public itemType: string;
  public forceDocumentType?: string;

  public constructor(itemType: string, forceDocumentType?: string) {
    this.itemType = itemType;
    this.forceDocumentType = forceDocumentType;
  }
}

/*
Hook to retrieve and validate itemId/itemType from params 
forceDocumentType and from search.

1. Retrieves itemId OR itemType from params + forceDocumentType from search.
   Gives priority to itemId

2. Applies common validation logic for item/item-type level pages,
   may throw ItemLevelNavigationError - which app handles trying to recover,
   with a fallback to /checklist (see ErrorBoundary). It also goes to Sentry

At item/item-type level pages (eg BasicDocument, ItemResolver, etc), we allow
app to use navigation either by itemId or itemType:
  - .../item/:itemId/...
  - .../item-type/:itemType/...

[!] For itemType, only TRAVEL_DOC item type is currently supported as part of
    the "new flow"
*/
const useItemLevelNavigationState = (
  searchParams: URLSearchParams
): ItemLevelNavigationState => {
  const { itemId, itemType } = useParams<{
    itemId?: string;
    itemType?: string;
  }>();

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("Invalid paxSession");
  }

  const paxSessionId = paxSession.paxSessionId;

  const forceDocumentType = searchParams.get("forceDocumentType") || undefined;

  const item = paxSession.checklist.items.find((item) => item.id === itemId);

  if (itemId && !item) {
    throw new ItemLevelNavigationError(
      sessionId,
      paxSessionId,
      `Item not found in checklist, item id: ${itemId}`
    );
  }

  if (itemId && item) {
    return new ItemNavigationState(item, forceDocumentType);
  }

  if (itemType) {
    if (itemType !== ChecklistItemType.TravelDoc) {
      // navigation by itemType is currently limited to TRAVEL_DOC
      throw new ItemLevelNavigationError(
        sessionId,
        paxSessionId,
        `Navigation by item type is currently only supported for TRAVEL_DOC item type, item type provided: ${itemType}`
      );
    }

    return new ItemTypeNavigationState(itemType, forceDocumentType);
  }

  // neither itemType or itemId
  throw new ItemLevelNavigationError(
    sessionId,
    paxSessionId,
    "Either itemId or itemType has to be provided"
  );
};

export default useItemLevelNavigationState;
